/* eslint-disable radix */
import React from "react";
import moment from "moment";
import { Row, Col, Alert } from "antd";
import { Subscription, Program } from "types/index";
import getCurrencySign from "utils/currency";
import outputManuallySetPrice from "utils/outputManuallySetPrice";
import resizeOverflowingPrice from "utils/resizeOverflowingPrice";
import {
  ProgramDescription,
  ProgramInfoContainer,
  SubscriptionStatusTag,
  Price,
  CurrencySign,
  PerUnitTime,
} from "common/program_selector";
import { H4 } from "common/text";
import { useTranslation } from "react-i18next";
import { useQuery } from "@apollo/client";
import GET_PROGRAMS from "graphql/queries/GetPrograms";
import GET_LANGUAGE_DETAILS from "graphql/queries/GetLanguageDetails";
import useLocale from "hooks/useLocale";
import GET_CURRENT_USER_INFO from "graphql/queries/GetCurrentUserInfo";
import {
  LittleBoxWrapper,
  LittleBoxHeader,
  LittleBoxDescription,
  TableProgramHeader,
} from "../styles";

type TMySubscriptionTable = {
  activeSubscription: Subscription;
  currency: string | undefined;
};

type TLittleBox = {
  header: string;
  description: string | number;
  date?: string;
  finalBox?: boolean; // if final box, then marginRight = -0.5px
  renew: boolean;
  currency?: string;
};

const LittleBox = ({
  header,
  description,
  date,
  finalBox = false,
  renew,
  currency,
}: TLittleBox) => {
  const textColor = renew ? "#1070b6" : "gray";
  return (
    <LittleBoxWrapper style={{ marginRight: finalBox ? -0 : -2 }} renew={renew}>
      <LittleBoxHeader renew={renew}>{header}</LittleBoxHeader>
      <LittleBoxDescription
        renew={renew}
        dangerouslySetInnerHTML={{
          __html: getCurrencySign(currency?.toString() || "").concat(
            description.toString()
          ),
        }}
      />
      {date && renew && (
        <p style={{ fontSize: 13, color: textColor, marginTop: -3 }}>{date}</p>
      )}
    </LittleBoxWrapper>
  );
};

const MySubscriptionTable = ({
  activeSubscription,
  currency,
}: TMySubscriptionTable) => {
  const { t } = useTranslation("my_credits");
  const language = useLocale();
  const { data, loading } = useQuery(GET_PROGRAMS, {
    variables: {
      currency,
      language,
      studyLanguageId: null,
    },
  });
  const { data: info } = useQuery(GET_CURRENT_USER_INFO);
  const { data: languageDetails } = useQuery(GET_LANGUAGE_DETAILS, {
    variables: {
      id: info?.me.currentLanguage.id || "1",
    },
  });

  const activeProgram: Program | undefined = data?.plans.find(
    (program: any) => program.id === activeSubscription.stripePriceId
  );

  const numberOfCredits = activeProgram?.credits || 0;

  const renderActiveSubscription = () => {
    const ActiveTag = (
      <SubscriptionStatusTag>{t("ACTIVE")}</SubscriptionStatusTag>
    );
    const PausedTag = (
      <SubscriptionStatusTag gray>{t("CANCELED")}</SubscriptionStatusTag>
    );
    const renderSubscriptionStatusTag = () => {
      if (activeSubscription?.renew) {
        return ActiveTag;
      }
      return PausedTag;
    };

    const textColor = activeSubscription?.renew ? "#1070b6" : "gray";

    return activeProgram ? (
      <TableProgramHeader
        key={activeProgram.id}
        value={activeProgram.id}
        checked
        renew={activeSubscription?.renew}
      >
        <ProgramInfoContainer gutter={[10, 10]}>
          <ProgramDescription sm={16} xs={12} style={{ padding: "5px" }}>
            <div style={{ whiteSpace: "normal", color: textColor }}>
              {activeProgram.name}
            </div>
            {renderSubscriptionStatusTag()}
          </ProgramDescription>
          <Price
            sm={8}
            xs={12}
            style={{
              fontSize: resizeOverflowingPrice(
                outputManuallySetPrice(activeProgram.price)
              ),
              color: textColor,
            }}
          >
            <CurrencySign>
              <span // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: getCurrencySign(currency?.toString() || ""),
                }}
              />
            </CurrencySign>
            {outputManuallySetPrice(activeProgram.price)}
            <PerUnitTime>{t("/month")}</PerUnitTime>
          </Price>
        </ProgramInfoContainer>
      </TableProgramHeader>
    ) : null;
  };

  const formattedDate =
    moment(new Date(activeSubscription.endDate)).format("MMM DD, YYYY") || "";

  const currentLanguage = info?.me.currentLanguage.englishName;

  const costCategory = languageDetails?.languageDetails.smallGroupCredit || 0;

  return loading ? (
    <Alert type="info" message={t("Loading...")} />
  ) : (
    <Row gutter={[16, 0]} style={{ marginTop: 10 }}>
      <Col xs={24} md={12}>
        <Row>
          {renderActiveSubscription()}
          <Col span={12}>
            <LittleBox
              description={formattedDate}
              header={
                activeSubscription?.renew ? "Renewal Date" : "Expiry Date"
              }
              renew={activeSubscription?.renew}
            />
          </Col>
          <Col span={6}>
            <LittleBox
              date={formattedDate}
              description={activeSubscription?.renew ? numberOfCredits : "0"}
              header="Next Credits"
              renew={activeSubscription?.renew}
            />
          </Col>
          <Col span={6}>
            <LittleBox
              date={formattedDate}
              currency={currency}
              description={
                activeProgram && activeSubscription?.renew
                  ? activeProgram?.price.toString()
                  : "0"
              }
              header="Next Payment"
              finalBox
              renew={activeSubscription?.renew}
            />
          </Col>
        </Row>
      </Col>
      <Col xs={24} md={12}>
        <H4>COSTS</H4>
        <p>
          {currentLanguage} Group Class:{" "}
          <strong>{costCategory} credits/class</strong>
          <br />
          {currentLanguage} 1-on-1 Class:{" "}
          <strong>
            {Number.parseFloat(costCategory || "0") * 3} credits/class
          </strong>
          <br />
          Credits are interchangeable between group/1-on-1 classes and
          languages.
        </p>
      </Col>
    </Row>
  );
};

export default MySubscriptionTable;
